import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const COLORS = {
  sensory: '#2493A2',
  integration: '#57BDA2',
  symbolic: '#DCB13C',
  capstone: '#E85D75',
};

interface Dot {
  id: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  pole: 'good' | 'bad';
}

export function ValenceMapper() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current) return;

    svg.selectAll('*').remove();

    const W = 320;
    const H = 180;
    const midX = W / 2;
    const midY = H / 2;

    const goodPole = { x: 55, y: midY };
    const badPole = { x: 265, y: midY };

    // Defs for gradient lines
    const defs = svg.append('defs');
    const gradGood = defs.append('linearGradient').attr('id', 'val-grad-good');
    gradGood.append('stop').attr('offset', '0%').attr('stop-color', COLORS.integration).attr('stop-opacity', 0.5);
    gradGood.append('stop').attr('offset', '100%').attr('stop-color', COLORS.integration).attr('stop-opacity', 0);

    const gradBad = defs.append('linearGradient').attr('id', 'val-grad-bad');
    gradBad.append('stop').attr('offset', '0%').attr('stop-color', COLORS.capstone).attr('stop-opacity', 0.5);
    gradBad.append('stop').attr('offset', '100%').attr('stop-color', COLORS.capstone).attr('stop-opacity', 0);

    // -- Pole labels --
    svg.append('text')
      .attr('x', goodPole.x)
      .attr('y', 28)
      .attr('text-anchor', 'middle')
      .attr('fill', COLORS.integration)
      .attr('font-size', '13px')
      .attr('font-weight', '700')
      .text('GOOD');

    svg.append('text')
      .attr('x', badPole.x)
      .attr('y', 28)
      .attr('text-anchor', 'middle')
      .attr('fill', COLORS.capstone)
      .attr('font-size', '13px')
      .attr('font-weight', '700')
      .text('BAD');

    // Pole aura circles
    svg.append('circle')
      .attr('cx', goodPole.x)
      .attr('cy', goodPole.y)
      .attr('r', 30)
      .attr('fill', COLORS.integration)
      .attr('opacity', 0.08);

    svg.append('circle')
      .attr('cx', badPole.x)
      .attr('cy', badPole.y)
      .attr('r', 30)
      .attr('fill', COLORS.capstone)
      .attr('opacity', 0.08);

    // "neutral zone" label (will fade)
    const neutralLabel = svg.append('text')
      .attr('x', midX)
      .attr('y', H - 14)
      .attr('text-anchor', 'middle')
      .attr('fill', 'var(--color-text-muted)')
      .attr('font-size', '8px')
      .attr('font-style', 'italic')
      .text('neutral zone (emptied)');

    // -- Generate dots in a neutral center cluster --
    const rng = d3.randomNormal(0, 18);
    const dots: Dot[] = Array.from({ length: 12 }, (_, i) => {
      const pole = i < 6 ? 'good' : 'bad';
      const poleTarget = pole === 'good' ? goodPole : badPole;
      const jitterX = (Math.random() - 0.5) * 40;
      const jitterY = (Math.random() - 0.5) * 50;
      return {
        id: i,
        startX: midX + rng(),
        startY: midY + rng(),
        targetX: poleTarget.x + jitterX,
        targetY: poleTarget.y + jitterY,
        pole,
      };
    });

    // -- Gravity lines group --
    const linesGroup = svg.append('g').attr('opacity', 0);

    dots.forEach(d => {
      const polePos = d.pole === 'good' ? goodPole : badPole;
      linesGroup.append('line')
        .attr('x1', polePos.x)
        .attr('y1', polePos.y)
        .attr('x2', d.targetX)
        .attr('y2', d.targetY)
        .attr('stroke', d.pole === 'good' ? COLORS.integration : COLORS.capstone)
        .attr('stroke-width', 0.7)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0.4);
    });

    // -- Draw dots --
    const dotGroup = svg.append('g');

    const circles = dotGroup.selectAll('circle')
      .data(dots)
      .enter()
      .append('circle')
      .attr('cx', d => d.startX)
      .attr('cy', d => d.startY)
      .attr('r', 5)
      .attr('fill', COLORS.sensory)
      .attr('opacity', 0.8);

    // -- Animate: after delay, dots drift to poles --
    let cancelled = false;

    function runAnimation() {
      if (cancelled) return;

      // Reset positions
      circles
        .attr('cx', d => d.startX)
        .attr('cy', d => d.startY)
        .attr('fill', COLORS.sensory)
        .attr('opacity', 0.8);

      linesGroup.attr('opacity', 0);
      neutralLabel.attr('opacity', 1);

      // Phase: drift to poles
      circles.transition()
        .delay(1200)
        .duration(1500)
        .ease(d3.easeCubicInOut)
        .attr('cx', d => d.targetX)
        .attr('cy', d => d.targetY)
        .attr('fill', d => d.pole === 'good' ? COLORS.integration : COLORS.capstone);

      // Show gravity lines during drift
      linesGroup.transition()
        .delay(1200)
        .duration(400)
        .attr('opacity', 1);

      // Fade neutral label
      neutralLabel.transition()
        .delay(1400)
        .duration(500)
        .attr('opacity', 0.4);

      // Hold, then restart
      setTimeout(() => {
        if (!cancelled) {
          linesGroup.transition().duration(300).attr('opacity', 0);
          circles.transition()
            .duration(600)
            .ease(d3.easeCubicInOut)
            .attr('cx', d => d.startX)
            .attr('cy', d => d.startY)
            .attr('fill', COLORS.sensory)
            .on('end', (_d, i2) => {
              if (i2 === 0 && !cancelled) {
                setTimeout(runAnimation, 400);
              }
            });
        }
      }, 4500);
    }

    const startTimeout = setTimeout(runAnimation, 300);

    return () => {
      cancelled = true;
      clearTimeout(startTimeout);
      svg.selectAll('*').interrupt();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-heart" /> Valence Mapper
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 180" />
      <p className="viz-container__caption">Emotion pre-labels experience.</p>
    </div>
  );
}
